// ==================== GURUKUL — AUDIT TRAIL (admin) ====================
// Read-only. "Actions" = who did what (plain sentences). "Data changes" = exactly which rows
// changed in each database (written by the database itself, so direct edits show up too).
import { showToast } from '../components/toasts.js';

function esc(text) {
  return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
async function api(path) {
  const res = await fetch(path, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

let kind = 'actions';
let page = 1;
let cache = [];

export function auditSectionHtml() {
  return `
    <div class="card" id="gk-sec-audit">
      <h2>Audit trail</h2>
      <p class="page-intro">A permanent, read-only record. Nothing here can be edited or deleted. Passwords are never stored.</p>
      <div class="gk-tabbar" id="gkAuditKinds">
        <button type="button" data-kind="actions" class="active">Actions</button>
        <button type="button" data-kind="gurukul_db">Data changes — Gurukul</button>
        <button type="button" data-kind="common_db">Data changes — Teams &amp; roles</button>
      </div>
      <div class="gk-filter-row" style="margin-top:10px;">
        <div class="form-group" id="gkAuditQWrap" style="flex:1;min-width:200px;"><label for="gkAuditQ">Search</label><input type="text" id="gkAuditQ" maxlength="80" placeholder="Person, request no., learner, words in the description"></div>
        <div class="form-group"><label for="gkAuditSel" id="gkAuditSelLabel">Action</label><select id="gkAuditSel"><option value="">All</option></select></div>
        <div class="form-group"><label for="gkAuditFrom">From</label><input type="date" id="gkAuditFrom"></div>
        <div class="form-group"><label for="gkAuditTo">To</label><input type="date" id="gkAuditTo"></div>
        <button type="button" id="gkAuditGo">Search</button>
      </div>
      <div class="table-container"><table class="table-stack-mobile">
        <thead id="gkAuditHead"></thead>
        <tbody id="gkAuditBody"><tr><td colspan="5" class="empty-state">Loading…</td></tr></tbody>
      </table></div>
      <div class="gk-pager"><span id="gkAuditInfo"></span><button type="button" class="secondary" id="gkAuditPrev">Prev</button><button type="button" class="secondary" id="gkAuditNext">Next</button></div>
    </div>
    <div id="gkAuditModal" class="modal"><div class="modal-content" style="max-width:760px;">
      <button type="button" class="close-modal" id="gkAuditClose">&times;</button>
      <h2 id="gkAuditModalTitle">Entry</h2><div id="gkAuditModalBody"></div>
    </div></div>`;
}

const fmt = d => new Date(d).toLocaleString();
const json = v => v == null ? '<em>—</em>' : `<pre style="white-space:pre-wrap;word-break:break-word;background:var(--bg-secondary);padding:8px;border-radius:6px;font-size:0.8rem;max-height:240px;overflow:auto;">${esc(JSON.stringify(v, null, 2))}</pre>`;

function changedKeys(o, n) {
  if (!o || !n) return null;
  return Object.keys(n).filter(k => JSON.stringify(o[k]) !== JSON.stringify(n[k]));
}

async function load() {
  const body = document.getElementById('gkAuditBody');
  const head = document.getElementById('gkAuditHead');
  const qs = new URLSearchParams({ kind, page: String(page) });
  const from = document.getElementById('gkAuditFrom').value;
  const to = document.getElementById('gkAuditTo').value;
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const sel = document.getElementById('gkAuditSel').value;
  if (kind === 'actions') {
    if (sel) qs.set('action', sel);
    const q = document.getElementById('gkAuditQ').value.trim();
    if (q) qs.set('q', q);
  } else if (sel) qs.set('table', sel);

  try {
    const d = await api('/api/gurukul/audit?' + qs.toString());
    cache = d.rows;
    page = d.page;
    const selEl = document.getElementById('gkAuditSel');
    const keep = selEl.value;
    const opts = kind === 'actions' ? d.actions : d.tables;
    selEl.innerHTML = '<option value="">All</option>' + opts.map(o => `<option value="${esc(o)}">${esc(o)}</option>`).join('');
    selEl.value = opts.includes(keep) ? keep : '';
    document.getElementById('gkAuditInfo').textContent = d.total ? `${(d.page - 1) * d.size + 1}–${Math.min(d.page * d.size, d.total)} of ${d.total}` : 'No entries';
    document.getElementById('gkAuditPrev').disabled = d.page <= 1;
    document.getElementById('gkAuditNext').disabled = d.page >= d.pages;

    if (kind === 'actions') {
      head.innerHTML = '<tr><th>When</th><th>Who</th><th>What</th><th>Action</th><th>Team</th></tr>';
      body.innerHTML = d.rows.length ? d.rows.map((r, i) => `
        <tr class="row-clickable" data-i="${i}">
          <td data-label="When">${esc(fmt(r.at))}</td><td data-label="Who">${esc(r.actor_name || 'system')}</td>
          <td data-label="What">${esc(r.summary)}</td><td data-label="Action"><code>${esc(r.action)}</code></td><td data-label="Team">${esc(r.team || '—')}</td>
        </tr>`).join('') : '<tr><td colspan="5" class="empty-state">No entries match.</td></tr>';
    } else {
      head.innerHTML = '<tr><th>When</th><th>Table</th><th>Change</th><th>Row</th><th>Fields changed</th></tr>';
      body.innerHTML = d.rows.length ? d.rows.map((r, i) => {
        const keys = changedKeys(r.old_row, r.new_row);
        return `<tr class="row-clickable" data-i="${i}">
          <td data-label="When">${esc(fmt(r.at))}</td><td data-label="Table">${esc(r.table_name)}</td>
          <td data-label="Change">${esc(r.op)}</td><td data-label="Row"><code>${esc(String(r.row_id || '').slice(0, 13))}</code></td>
          <td data-label="Fields changed">${esc(keys ? keys.join(', ') : (r.op === 'INSERT' ? 'new row' : 'row removed'))}</td></tr>`;
      }).join('') : '<tr><td colspan="5" class="empty-state">No entries match.</td></tr>';
    }
    body.querySelectorAll('tr[data-i]').forEach(tr => tr.addEventListener('click', () => openEntry(cache[Number(tr.dataset.i)])));
  } catch (err) {
    body.innerHTML = `<tr><td colspan="5" class="empty-state">Failed to load: ${esc(err.message)}</td></tr>`;
  }
}

function openEntry(r) {
  const modal = document.getElementById('gkAuditModal');
  const title = document.getElementById('gkAuditModalTitle');
  const body = document.getElementById('gkAuditModalBody');
  if (kind === 'actions') {
    title.textContent = r.action;
    body.innerHTML = `
      <p><strong>${esc(r.summary)}</strong></p>
      <div class="table-container"><table class="table-stack-mobile"><tbody>
        <tr><th style="width:32%;text-align:left;">When</th><td>${esc(fmt(r.at))}</td></tr>
        <tr><th style="text-align:left;">Who</th><td>${esc(r.actor_name || 'system')}${r.actor_ip ? ' · ' + esc(r.actor_ip) : ''}</td></tr>
        <tr><th style="text-align:left;">About</th><td>${esc(r.entity_type || '')} ${esc(r.entity_label || r.entity_id || '')}</td></tr>
        <tr><th style="text-align:left;">Team</th><td>${esc(r.team || '—')}</td></tr>
      </tbody></table></div>
      <h3>Before</h3>${json(r.before_data)}<h3>After / details</h3>${json(r.after_data)}`;
  } else {
    title.textContent = `${r.op} on ${r.table_name}`;
    const keys = changedKeys(r.old_row, r.new_row);
    body.innerHTML = `
      <p>${esc(fmt(r.at))} · database user <code>${esc(r.db_user)}</code></p>
      ${keys ? `<p><strong>Changed:</strong> ${esc(keys.join(', '))}</p>` : ''}
      <h3>Before</h3>${json(r.old_row)}<h3>After</h3>${json(r.new_row)}`;
  }
  modal.classList.add('active');
}

export function initAuditSection() {
  const reload = () => { page = 1; load(); };
  document.querySelectorAll('#gkAuditKinds button').forEach(b => b.addEventListener('click', () => {
    kind = b.dataset.kind;
    document.querySelectorAll('#gkAuditKinds button').forEach(x => x.classList.toggle('active', x === b));
    document.getElementById('gkAuditQWrap').style.display = kind === 'actions' ? '' : 'none';
    document.getElementById('gkAuditSelLabel').textContent = kind === 'actions' ? 'Action' : 'Table';
    document.getElementById('gkAuditSel').innerHTML = '<option value="">All</option>';
    reload();
  }));
  document.getElementById('gkAuditGo').addEventListener('click', reload);
  document.getElementById('gkAuditQ').addEventListener('keydown', e => { if (e.key === 'Enter') reload(); });
  ['gkAuditSel', 'gkAuditFrom', 'gkAuditTo'].forEach(id => document.getElementById(id).addEventListener('change', reload));
  document.getElementById('gkAuditPrev').addEventListener('click', () => { page = Math.max(page - 1, 1); load(); });
  document.getElementById('gkAuditNext').addEventListener('click', () => { page += 1; load(); });
  document.getElementById('gkAuditClose').addEventListener('click', () => document.getElementById('gkAuditModal').classList.remove('active'));
  load().catch(err => showToast(err.message, 'error'));
}
